/**
 * The design controls: one compact row, and at most one row of options under it.
 *
 * The rule this file exists to obey is *progressive disclosure*. A design is edited
 * by looking at it, so the controls must not become the screen: an author reaches for
 * four things while designing — templates, the picture, the background, the text —
 * and while they are writing, for nothing at all. So the toolbar is one row, and
 * pressing a chip opens *one* row of options beneath it, which closes again when it is
 * pressed a second time. Nothing is a panel, nothing is a sheet, and nothing covers
 * the design.
 *
 * The row is *contextual*. The moment the author selects words it becomes the things
 * that can be done to words — highlight, Kashida, Mursala, weight, size — because
 * that is plainly what the author is doing now. Let the selection go and the design's
 * own chips come back. Both rows scroll sideways, so a phone shows a chip and a half
 * without ever wrapping to a second line.
 *
 * Two rules keep the number of presses down, because every press is a moment spent
 * not looking at the design:
 *
 *  - **the colour of a highlight is chosen by *being* the highlight.** While words are
 *    selected the palette is in the row itself, each swatch painting the words in one
 *    press — the colour is not a setting to open first and apply afterwards;
 *  - **a chip says what the design already is**, so the author does not have to open a
 *    section to find out: the text chip wears the face and size it would offer, the
 *    background chip wears the colour behind the words, the template chip names the
 *    template in use, and the size chip carries the number.
 *
 * The picture is the post's own: pressing *Picture* opens the very picker the
 * composer's media button opens (`SelectMediaButton`), so a design's photograph is
 * picked, shown, kept in a draft and uploaded exactly once, the way every other
 * picture in a post is — and this file never touches a blob.
 *
 * Where it is *placed* matters as much as what is on it: it is rendered in the
 * composer's footer, inside the `KeyboardAvoidingView`, which is what makes it float
 * directly above the keyboard on a phone and sit at the bottom of the composer on the
 * web — the same place the composer's own controls have always been, rather than a
 * surface of its own.
 */
import {useCallback, useEffect, useState} from 'react'
import {ScrollView, Text as RNText, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {PostDesignRenderer} from '#/components/Post/PostDesignRenderer'
import {Text} from '#/components/Typography'
import {atoms as a, useTheme} from '#/alf'
import {thmanyahFontFamily} from '#/state/postStyle/fonts'
import {DESIGN_CANVAS_DEFAULT_SIZE} from '#/state/postStyle/layout'
import {
  HIGHLIGHT_PALETTE,
  STYLE_PALETTE,
  STYLE_TEMPLATES,
  applyStyledTemplate,
} from '#/state/postStyle/templates'
import {toggleHighlight, toggleMursala} from '#/state/postStyle/typography'
import {
  type StyledPost,
  type StyledPostWeight,
  STYLED_POST_MAX_SIZE,
  STYLED_POST_MIN_SIZE,
} from '#/state/postStyle/types'
import {type PostAction} from '#/view/com/composer/state/composer'
import {StylizerChip, StylizerSwatch} from './StylizerBar'
import {stylizerRange} from './selection'

/** How much one press of *smaller* or *bigger* changes the size, in points. */
const SIZE_STEP = 4

/** Which row of options is open under the context row, if any. */
type Section = 'none' | 'templates' | 'background' | 'text' | 'size' | 'more'

/** The words a template chip previews itself with, before the author has written any. */
const PREVIEW_TEXT = 'أبجد'

/** The colour of a design's words when the author has not chosen one. */
const DEFAULT_INK = '#1A1A1A'

export function DesignToolbar({
  style,
  text,
  selection,
  dispatch,
  onError,
  onClose,
  onPickPicture,
}: {
  style: StyledPost
  /** The post's words, exactly as the canvas holds them. */
  text: string
  /** Where the author's selection is, or `null` when the input has not said. */
  selection: {start: number; end: number} | null
  dispatch: (action: PostAction) => void
  onError: (message: string) => void
  onClose: () => void
  /**
   * Ask for a picture — the composer's own media picker, for the post this design
   * belongs to.
   *
   * A function rather than a picker of this component's own:  a design's picture is
   * the *post's* picture, and the composer is the only thing that knows how this app
   * picks one, where the file on this device is kept, and when it is uploaded.
   * Nothing about a blob is decided here.
   */
  onPickPicture?: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const [section, setSection] = useState<Section>('none')

  const hasSelection = !!selection && selection.end > selection.start
  const size = style.size ?? DESIGN_CANVAS_DEFAULT_SIZE
  const template =
    STYLE_TEMPLATES.find(entry => entry.id === style.template) ?? null
  /*
   * Names, written out rather than built from ids, because a name is a word a
   * translator needs and an id is not. The template's is worn by the chip that opens
   * the templates, so an author can see which one the design is *using* without
   * opening anything.
   */
  const templateNames: Record<string, string> = {
    ramadan: l`Ramadan`,
    cinema: l`Cinema`,
    quote: l`Quote`,
    portrait: l`Portrait`,
    editorial: l`Editorial`,
  }
  const weightNames: Record<StyledPostWeight, string> = {
    regular: l`Regular`,
    bold: l`Bold`,
    black: l`Black`,
  }
  /* What the design is set in, for the chip that offers to change it. */
  const face = weightNames[style.weight ?? 'bold']

  /* A new context is a new set of controls: the open row goes with the old one. */
  useEffect(() => {
    setSection('none')
  }, [hasSelection])

  const paint = useCallback(
    (next: StyledPost) => {
      dispatch({type: 'style_update', style: next})
    },
    [dispatch],
  )

  const rangeFor = useCallback(
    (target: 'word' | 'sentence') => stylizerRange(text, selection, target),
    [text, selection],
  )

  const onHighlight = useCallback(() => {
    const range = rangeFor('sentence')
    if (!range) {
      onError(l`Select the words you want highlighted.`)
      return
    }
    paint(
      toggleHighlight(
        style,
        range,
        style.highlightColor ?? HIGHLIGHT_PALETTE[0],
      ),
    )
  }, [rangeFor, onError, l, paint, style])

  /**
   * Paint the words in one colour, in one press.
   *
   * While words are selected, choosing a highlight colour and applying it are the
   * same decision, so they are the same press: the swatch *is* the action. The
   * colour is kept on the card as well (`highlightColor`), so the next selection
   * starts where this one ended.
   */
  const highlightWith = useCallback(
    (color: string) => {
      const range = rangeFor('sentence')
      if (!range) {
        onError(l`Select the words you want highlighted.`)
        return
      }
      paint(toggleHighlight({...style, highlightColor: color}, range, color))
    },
    [rangeFor, onError, l, paint, style],
  )

  const onMursala = useCallback(() => {
    const range = rangeFor('word')
    if (!range) {
      onError(l`Put the cursor inside a word to give it Mursala.`)
      return
    }
    paint(toggleMursala(style, text, range))
  }, [rangeFor, onError, l, paint, style, text])

  const onKashida = useCallback(() => {
    const range = rangeFor('word')
    if (!range) {
      onError(l`Put the cursor inside a word to stretch it.`)
      return
    }
    /* A Kashida edits the words, so it is dispatched as an edit to them. */
    dispatch({type: 'kashida', range})
  }, [rangeFor, onError, l, dispatch])

  const openSection = (next: Section) =>
    setSection(current => (current === next ? 'none' : next))

  const stepTo = (direction: -1 | 1) =>
    paint({
      ...style,
      size: Math.min(
        STYLED_POST_MAX_SIZE,
        Math.max(STYLED_POST_MIN_SIZE, size + direction * SIZE_STEP),
      ),
    })

  const chipRow = [
    a.flex_row,
    a.align_center,
    a.gap_xs,
    {paddingHorizontal: 12},
  ]
  const optionsRow = [chipRow, a.pb_sm]

  const weightChip = (weight: StyledPostWeight, label: string) => (
    <StylizerChip
      key={weight}
      testID={`designWeight-${weight}`}
      label={label}
      selected={(style.weight ?? 'bold') === weight}
      onPress={() => paint({...style, weight})}>
      {/*
       * The chip wears the face it offers, so the choice is made by looking at it —
       * and it is drawn by React Native's own `Text`, because the app's text
       * component would re-type it in Inter and offer three identical chips.
       */}
      <RNText
        style={{
          color: t.atoms.text.color,
          fontSize: 16,
          fontFamily: thmanyahFontFamily(weight),
        }}>
        {PREVIEW_TEXT}
      </RNText>
    </StylizerChip>
  )

  const sizeChips = (testID: string) => [
    <StylizerChip
      key="smaller"
      testID={`${testID}Down`}
      label={l`Smaller text`}
      onPress={() => stepTo(-1)}>
      <Text style={[a.text_md, t.atoms.text]}>{'\u2212'}</Text>
    </StylizerChip>,
    <Text
      key="size"
      style={[
        a.text_sm,
        t.atoms.text_contrast_medium,
        {minWidth: 28, textAlign: 'center'},
      ]}>
      {size}
    </Text>,
    <StylizerChip
      key="bigger"
      testID={`${testID}Up`}
      label={l`Bigger text`}
      onPress={() => stepTo(1)}>
      <Text style={[a.text_md, t.atoms.text]}>{'+'}</Text>
    </StylizerChip>,
  ]

  return (
    <View
      testID="designToolbar"
      style={[t.atoms.bg, a.border_t, t.atoms.border_contrast_low]}>
      <ScrollView
        horizontal
        bounces={false}
        keyboardShouldPersistTaps="always"
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[chipRow, a.py_xs]}>
        {hasSelection ? (
          <>
            {/* The highlight, and every colour it can be — one press each. */}
            <StylizerChip
              testID="designHighlight"
              label={l`Highlight`}
              onPress={onHighlight}
            />
            {HIGHLIGHT_PALETTE.map(color => (
              <StylizerSwatch
                key={`hl-${color}`}
                testID={`designHighlightColour-${color}`}
                color={color}
                label={l`Highlight in this colour`}
                selected={
                  (style.highlightColor ?? HIGHLIGHT_PALETTE[0]) === color
                }
                onPress={() => highlightWith(color)}
              />
            ))}
            <StylizerChip
              testID="designKashida"
              label={l`Kashida`}
              onPress={onKashida}
            />
            <StylizerChip
              testID="designMursala"
              label={l`Mursala`}
              onPress={onMursala}
            />
            {weightChip('bold', l`Bold`)}
            <StylizerChip
              testID="designContextSize"
              label={l`Size`}
              selected={section === 'size'}
              onPress={() => openSection('size')}>
              <ChipState text={`${l`Size`} · ${size}`} />
            </StylizerChip>
            <StylizerChip
              testID="designContextMore"
              label={l`More`}
              selected={section === 'more'}
              onPress={() => openSection('more')}
            />
          </>
        ) : (
          <>
            <StylizerChip
              testID="designTemplates"
              label={l`Templates`}
              selected={section === 'templates'}
              onPress={() => openSection('templates')}>
              {template ? (
                <ChipState
                  text={`${l`Templates`} · ${templateNames[template.id] ?? template.id}`}
                />
              ) : undefined}
            </StylizerChip>
            {/*
             * The picture, in one press and where the design's other decisions are:
             * pressing it opens the composer's own media picker for this post — the
             * same one its media button opens — so a design is given a photograph the
             * way a post is, and nothing about a file or a blob is decided here.
             */}
            <StylizerChip
              testID="designPicture"
              label={l`Picture`}
              selected={style.background.type === 'image'}
              onPress={() => onPickPicture?.()}
            />
            {style.background.type === 'image' ? (
              <StylizerChip
                testID="designPictureRemove"
                label={l`Remove the picture`}
                onPress={() =>
                  paint({
                    ...style,
                    background: {type: 'solid', color: style.background.color},
                  })
                }
              />
            ) : null}
            <StylizerChip
              testID="designBackground"
              label={l`Background`}
              selected={section === 'background'}
              onPress={() => openSection('background')}>
              <View style={[a.flex_row, a.align_center, {gap: 6}]}>
                {/* The colour behind the words, worn by the chip that changes it. */}
                <View
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 7,
                    backgroundColor: style.background.color,
                  }}
                />
                <ChipState text={l`Background`} />
              </View>
            </StylizerChip>
            <StylizerChip
              testID="designText"
              label={l`Text`}
              selected={section === 'text'}
              onPress={() => openSection('text')}>
              {/* What the words are set in, on the chip that offers to change it. */}
              <ChipState text={`${face} · ${size}`} />
            </StylizerChip>
            <StylizerChip
              testID="designMore"
              label={l`More`}
              selected={section === 'more'}
              onPress={() => openSection('more')}
            />
          </>
        )}
      </ScrollView>

      {section === 'templates' ? (
        <ScrollView
          horizontal
          bounces={false}
          keyboardShouldPersistTaps="always"
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={optionsRow}>
          {STYLE_TEMPLATES.map(entry => (
            <StylizerChip
              key={entry.id}
              testID={`designTemplate-${entry.id}`}
              label={templateNames[entry.id] ?? entry.id}
              selected={style.template === entry.id}
              onPress={() => paint(applyStyledTemplate(style, entry))}
              style={{
                borderRadius: 16,
                paddingHorizontal: 3,
                paddingVertical: 3,
              }}>
              {/*
               * A template is shown, not named: the chip *is* a small design, drawn
               * by the same renderer the feed uses, with the author's own words on
               * it. Choosing one is choosing what you can see.
               */}
              <PostDesignRenderer
                testID={`designTemplatePreview-${entry.id}`}
                style={applyStyledTemplate(style, entry)}
                text={text.trim() ? text.slice(0, 18) : PREVIEW_TEXT}
                minHeight={76}
              />
            </StylizerChip>
          ))}
        </ScrollView>
      ) : null}

      {section === 'background' ? (
        <ScrollView
          horizontal
          bounces={false}
          keyboardShouldPersistTaps="always"
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={optionsRow}>
          {STYLE_PALETTE.map(color => (
            <StylizerSwatch
              key={`bg-${color}`}
              testID={`designBackground-${color}`}
              color={color}
              label={l`Background colour`}
              selected={style.background.color === color}
              onPress={() =>
                paint({...style, background: {type: 'solid', color}})
              }
            />
          ))}
          <StylizerChip
            testID="designGrain"
            label={l`Grain`}
            selected={!!style.texture}
            onPress={() =>
              paint({...style, texture: style.texture ? undefined : true})
            }
          />
        </ScrollView>
      ) : null}

      {section === 'text' ? (
        <ScrollView
          horizontal
          bounces={false}
          keyboardShouldPersistTaps="always"
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={optionsRow}>
          {weightChip('regular', l`Regular`)}
          {weightChip('bold', l`Bold`)}
          {weightChip('black', l`Black`)}
          {sizeChips('designSize')}
          {STYLE_PALETTE.map(color => (
            <StylizerSwatch
              key={`ink-${color}`}
              testID={`designInk-${color}`}
              color={color}
              round
              label={l`Text colour`}
              selected={(style.ink ?? DEFAULT_INK) === color}
              onPress={() => paint({...style, ink: color})}
            />
          ))}
          <StylizerChip
            testID="designAlign"
            label={l`Centred`}
            selected={style.align === 'center'}
            onPress={() =>
              paint({
                ...style,
                align: style.align === 'center' ? 'start' : 'center',
              })
            }
          />
        </ScrollView>
      ) : null}

      {section === 'size' ? (
        <ScrollView
          horizontal
          bounces={false}
          keyboardShouldPersistTaps="always"
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={optionsRow}>
          {sizeChips('designSizeCompact')}
        </ScrollView>
      ) : null}

      {section === 'more' ? (
        <ScrollView
          horizontal
          bounces={false}
          keyboardShouldPersistTaps="always"
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={optionsRow}>
          {template?.pathsEditable
            ? STYLE_PALETTE.map(color => (
                <StylizerSwatch
                  key={`path-${color}`}
                  testID={`designPathColour-${color}`}
                  color={color}
                  label={l`Template colour`}
                  selected={style.templateValues?.pathColor === color}
                  onPress={() =>
                    paint({
                      ...style,
                      templateValues: {
                        ...style.templateValues,
                        pathColor: color,
                      },
                    })
                  }
                />
              ))
            : null}
          <StylizerChip
            testID="designHighlightClear"
            label={l`Clear highlights`}
            onPress={() => paint({...style, highlights: undefined})}
          />
          <StylizerChip
            testID="designMursalaClear"
            label={l`Clear Mursala`}
            onPress={() => paint({...style, mursalas: undefined})}
          />
          <StylizerChip
            testID="designKashidaClear"
            label={l`Remove the Kashidas`}
            onPress={() => dispatch({type: 'kashida_clear'})}
          />
          <StylizerChip
            testID="designTurnOff"
            label={l`Turn the design off`}
            onPress={onClose}
          />
        </ScrollView>
      ) : null}
    </View>
  )
}

/**
 * What a chip says when it is *telling* the author something rather than offering to
 * change it: the design's current setting, in the app's own type and dimmer than a
 * label, so a glance down the row reads as the state of the design.
 */
function ChipState({text}: {text: string}) {
  const t = useTheme()
  return <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>{text}</Text>
}
